import React, { FC } from 'react'
import { Box, Text, Link, Spinner, Center, useToast } from '@chakra-ui/react'
import Page from '../../components/Page'
import { useGetAvailableIntegrationsQuery, useConnectIntegrationMutation } from './integrationSlice'
import { AvailableIntegrationsList } from './components/AvailableIntegrationsList'
import { useAppSelector } from '../../app/typedReduxHooks'
import { RootState } from '../../app/store'

export const IntegrationsPage: FC<unknown> = () => {
  const { data: integrationProviders, isLoading, error } = useGetAvailableIntegrationsQuery()
  const prescriber = useAppSelector(({ prescriber }: RootState) => prescriber?.prescriber)
  const isEntityIdExist = !!prescriber?.entityId
  const [connectIntegration] = useConnectIntegrationMutation()
  const [connectingId, setConnectingId] = React.useState<string>()
  const toast = useToast()

  const handleConnect = async (integrationProviderId: string) => {
    try {
      if (!isEntityIdExist) {
        toast({
          title: 'Please complete your prescriber profile to connect an integration',
          status: 'error',
          duration: 3000,
          isClosable: true,
        })
        return
      }

      setConnectingId(integrationProviderId)
      await connectIntegration(integrationProviderId).unwrap()
      toast({
        title: 'Integration connected',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error: any) {
      toast({
        title:
          error.data?.code === 'USER_NOT_FOUND_IN_INTEGRATION_PROVIDER'
            ? `The email address you used to sign up for RxPad doesn't match your integration account. Please sign up for the integration if you haven't already, or contact support@rxpad.com.au if you're using a different email address. (${error.data?.message})`
            : 'Failed to connect integration',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setConnectingId(undefined)
    }
  }

  if (isLoading) {
    return (
      <Page alignItems="center">
        <Center h="100vh">
          <Spinner size="xl" />
        </Center>
      </Page>
    )
  }

  if (error) {
    return (
      <Page alignItems="center">
        <Center h="100vh">
          <Text color="red.500">Failed to load integrations</Text>
        </Center>
      </Page>
    )
  }

  return (
    <Page alignItems="center">
      <Box w="100%" mt={8} px={[2, 4, 8]}>
        <Text fontSize="2xl" fontWeight="bold" mb={6}>
          Available Integrations
        </Text>
        <AvailableIntegrationsList
          integrationProviders={integrationProviders || []}
          onConnect={handleConnect}
          connectingId={connectingId}
        />
        <Text mt={12} color="gray.400" textAlign="center" fontSize="sm">
          Need help with integrations? Contact support at{' '}
          <Link color="blue.500" href="mailto:support@rxpad.com.au">
            support@rxpad.com.au
          </Link>
        </Text>
      </Box>
    </Page>
  )
}
